<template>
    <div v-if="center" class="map-component">
        <l-map
            ref="map"
            v-model="zoom"
            class="map-component__map"
            :zoom="zoom"
            :center="center"
            :maxZoom="18"
            @ready="onMapReady"
            @dragend="onMapMove($event)"
        >
            <l-tile-layer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="© <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
            ></l-tile-layer>
            <l-control-layers></l-control-layers>
            <marker-cluster :options="{ showCoverageOnHover: false, chunkedLoading: true }">
                <l-marker
                    v-for="(marker, index) in markers"
                    :ref="`marker${marker.id}`"
                    :key="index"
                    :icon="getIcon(marker.locationType)"
                    :latLng="marker.coordinates"
                    :draggable="isMarkerDraggable"
                    @drag="markerDrag($event, marker)"
                >
                    <l-popup class="map-component__popup">
                        <map-marker-tooltip-parking
                            v-if="marker.locationType === LocationTypeEnum.PARKING"
                            :marker="marker"
                        ></map-marker-tooltip-parking>
                        <map-marker-tooltip v-else :marker="marker"></map-marker-tooltip>
                    </l-popup>
                </l-marker>
            </marker-cluster>
        </l-map>
    </div>
</template>
<script>
    import { LMap, LTileLayer, LMarker, LControlLayers, LPopup } from '@vue-leaflet/vue-leaflet'
    import 'leaflet/dist/leaflet.css'
    import debounce from 'lodash.debounce'
    import MarkerCluster from 'components/page-elements/locations/map/MarkerCluster'
    import MapMarkerTooltip from 'components/page-elements/locations/map/MapMarkerTooltip'
    import MapMarkerTooltipParking from 'components/page-elements/locations/map/MapMarkerTooltipParking'
    import LocationTypeEnum from 'enums/location_type.enum'
    import L from 'leaflet'

    export default {
        components: {
            MarkerCluster,
            LMap,
            LTileLayer,
            LMarker,
            LControlLayers,
            LPopup,
            MapMarkerTooltip,
            MapMarkerTooltipParking,
        },

        props: {
            mapCenter: { type: Object, default: () => ({ lat: 44.8125449, lng: 20.46123 }) },
            markers: { type: Array, default: new Array() },
            isMarkerDraggable: { type: Boolean, default: false },
            defaultZoom: { type: Number, default: 11 },
        },

        data() {
            return {
                center: null,
                zoom: 13,
                LocationTypeEnum: LocationTypeEnum,
                customIcon: L.icon({
                    iconUrl: require('../../../../assets/images/parking.png'),
                    iconSize: [35, 35],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                }),
                leafletMap: undefined,
                isOpenPopupActive: false,
            }
        },

        methods: {
            markerDrag: debounce(function (event, marker) {
                this.emitEvent({
                    name: 'marker-drag',
                    payload: { oldCoordinates: marker.coordinates, newCoordinates: event.latlng },
                })
            }, 600),

            getIcon(locationType) {
                return locationType === this.LocationTypeEnum.PARKING ? this.customIcon : undefined
            },

            onMapReady(map) {
                this.leafletMap = map
            },

            onMapMove() {
                if (this.leafletMap) {
                    const center = this.leafletMap.getCenter()
                    const bounds = this.leafletMap.getBounds()
                    const corner = bounds.getNorthEast()
                    const distance = center.distanceTo(corner) * 0.8

                    this.emitEvent({
                        name: 'map-position-changed',
                        payload: { center, distance },
                    })
                }
            },
        },

        watch: {
            mapCenter(newValue, oldValue) {
                if (this.isOpenPopupActive) return
                if (newValue.lat !== oldValue.lat && newValue.lng !== oldValue.lng) {
                    setTimeout(() => (this.center = newValue), 300)
                }
                setTimeout(() => (this.zoom = this.defaultZoom), 400)
            },
        },

        mounted() {
            this.center = this.mapCenter
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/mixins';

    .map-component {
        height: 100%;
        z-index: 1;

        &__map {
            z-index: 1;
            min-height: 500px;
        }

        &__popup {
            min-width: 150px;
            @include breakpoint(smartphone-large) {
                max-width: 250px;
            }

            &--text {
                font-size: 14px;
                font-weight: 600;
                text-align: center;
            }
        }
    }

    .subdomain-layout {
        .map-component__map {
            border-radius: 15px !important;
        }
    }
</style>
